import type { AudioEngine } from './engine.js';

/**
 * The page's side of the sound (design, "Desktop preview" and "The demo, the
 * played lesson and the teacher's preview"): the first tap or key lets the
 * context play; `M`, a corner button and the start card's toggle mute it for
 * this viewer (`?sound=0` starts muted), kept for the page's life in
 * `localStorage` as a convenience only; hidden, it fades and suspends.
 */

export const MUTE_KEY = 'vrlearning.sound.muted';

type Store = { getItem(key: string): string | null; setItem(key: string, value: string): void };

/** The browser's storage, or none where it is refused. */
export function browserStore(): Store | undefined {
  try {
    return globalThis.localStorage ?? undefined;
  } catch {
    return undefined;
  }
}

/** Muted at the start: `?sound=0`, or as this viewer left it (`?sound=1` overrides that). */
export function mutedAtStart(href: string, storage: Store | undefined): boolean {
  let asked: string | null = null;
  try {
    asked = new URL(href).searchParams.get('sound');
  } catch {
    // Not a URL: nothing asked.
  }
  if (asked === '0') return true;
  if (asked === '1') return false;
  try {
    return storage?.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

export function rememberMuted(muted: boolean, storage: Store | undefined): void {
  try {
    storage?.setItem(MUTE_KEY, muted ? '1' : '0');
  } catch {
    // A private window: the choice lasts as long as the page.
  }
}

export const soundLabel = (muted: boolean): string => (muted ? 'Geluid: uit' : 'Geluid: aan');
export const soundTitle = (muted: boolean): string =>
  muted ? 'Geluid aanzetten (M)' : 'Geluid uitzetten (M)';
export const START_NOTE = 'Klik om geluid te starten';

/**
 * The preview's note on recordings that did not play: not loaded (offline,
 * never fetched), or loaded but not playable here (Safari may not decode Ogg Opus).
 */
export function missingNote({
  unloaded,
  unplayable,
}: {
  unloaded: number;
  unplayable: number;
}): string {
  const parts = [
    ...(unloaded > 0 ? [`${unloaded} ${unloaded === 1 ? 'geluid' : 'geluiden'} niet geladen`] : []),
    ...(unplayable > 0
      ? [
          `${unplayable} ${unplayable === 1 ? 'bestand' : 'bestanden'} niet af te spelen in deze browser`,
        ]
      : []),
  ];
  return `Geluid: ${parts.join(', ')}`;
}

/**
 * The preview's note on voice-overs that did not play (ADR-033): a step's
 * file not loaded (offline, never cached), or not playable in this browser.
 * Its button hides for that step.
 */
export function voiceNote({
  unloaded,
  unplayable,
}: {
  unloaded: number;
  unplayable: number;
}): string {
  const steps = (count: number): string => `${count} ${count === 1 ? 'stap' : 'stappen'}`;
  const parts = [
    ...(unloaded > 0 ? [`${steps(unloaded)} niet geladen`] : []),
    ...(unplayable > 0 ? [`${steps(unplayable)} niet af te spelen in deze browser`] : []),
  ];
  return `Voorlezen: ${parts.join(', ')}`;
}

type KeyEvent = Pick<KeyboardEvent, 'key' | 'repeat' | 'metaKey' | 'ctrlKey' | 'altKey' | 'target'>;

/** A plain M, not a shortcut, not held down, not typed into a field. */
export function isMuteKey(event: KeyEvent): boolean {
  return isPlainKey(event, 'm');
}

/** A plain V: "Voorlezen" in the desktop preview, as R is "Nog eens" (not Cmd+V, paste). */
export function isVoiceKey(event: KeyEvent): boolean {
  return isPlainKey(event, 'v');
}

/** A plain G: the guide, text or voice, in the desktop preview outside a class (ADR-035). */
export function isGuideKey(event: KeyEvent): boolean {
  return isPlainKey(event, 'g');
}

/** The start card's toggle for a played lesson and the demo: "Begeleiding: tekst / stem". */
export const guideLabel = (guide: 'text' | 'voice'): string =>
  guide === 'voice' ? 'Begeleiding: stem' : 'Begeleiding: tekst';

function isPlainKey(event: KeyEvent, letter: string): boolean {
  if (event.key.toLowerCase() !== letter) return false;
  if (event.repeat || event.metaKey || event.ctrlKey || event.altKey) return false;
  const target = event.target as { tagName?: string; isContentEditable?: boolean } | null;
  if (!target) return true;
  return (
    !target.isContentEditable && !['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName ?? '')
  );
}

/** Where this page keeps the viewer's mute: a played lesson and the demo only (installAudioControls). */
let muteStorage: Store | undefined;

/**
 * The page's listeners, for the page's life: the first pointer or key lets
 * the context play (a headset's "Start in VR" tap is one), `M` mutes, and a
 * hidden page fades out and suspends.
 */
export function installAudioControls(audio: AudioEngine, options: { storage?: Store } = {}): void {
  // Remembered only where given: a class headset never keeps a mute from a preview.
  muteStorage = options.storage;
  // A touch or pen tap activates the page at its end, not at pointerdown: all of them.
  const gestures = ['pointerdown', 'pointerup', 'click', 'touchend', 'keydown'] as const;
  const unlock = (): void => audio.unlock();
  for (const gesture of gestures) window.addEventListener(gesture, unlock, true);
  audio.onUnlocked(() => {
    for (const gesture of gestures) window.removeEventListener(gesture, unlock, true);
  });
  window.addEventListener('keydown', (event) => {
    if (isMuteKey(event)) toggleMute(audio);
  });
  document.addEventListener('visibilitychange', () => {
    audio.setVisibility(document.hidden ? 'hidden' : 'visible');
  });
}

const muteHandlers = new Set<(muted: boolean) => void>();

/** Mutes or unmutes for this viewer, remembers it, and tells every button. */
export function toggleMute(audio: AudioEngine): void {
  const muted = !audio.localMute;
  audio.setLocalMute(muted);
  rememberMuted(muted, muteStorage);
  for (const handler of muteHandlers) handler(muted);
}

/**
 * A button that shows the sound's state and switches it: in a corner of the
 * desktop preview ("Klik om geluid te starten" until a click lets it play), or
 * on a played lesson's start card, under "Start in VR". Removed with `remove`.
 */
export function soundButton(
  audio: AudioEngine,
  place: 'corner' | 'card',
): { element: HTMLButtonElement; remove: () => void } {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = place === 'card' ? 'lesson sound-toggle' : 'lesson sound-corner';
  const show = (): void => {
    const waiting = place === 'corner' && !audio.unlocked;
    button.textContent = waiting ? START_NOTE : soundLabel(audio.localMute);
    button.title = waiting ? START_NOTE : soundTitle(audio.localMute);
    button.setAttribute('aria-pressed', String(!audio.localMute));
  };
  const onMute = (): void => show();
  muteHandlers.add(onMute);
  const stopWaiting = audio.onUnlocked(show);
  // The corner's first click is the one that lets the sound play, not a mute:
  // told at the press, before the context has had time to start.
  let starting = false;
  button.addEventListener('pointerdown', () => {
    starting = place === 'corner' && !audio.unlocked;
  });
  button.addEventListener('click', (event) => {
    if (starting || (place === 'corner' && !audio.unlocked)) {
      starting = false;
      audio.unlock();
      return;
    }
    event.stopPropagation();
    toggleMute(audio);
  });
  // In the corner, main.ts puts it in the top-right stack under the preview's notice (index.html).
  show();
  return {
    element: button,
    remove: () => {
      muteHandlers.delete(onMute);
      stopWaiting();
      button.remove();
    },
  };
}
